import { Archive, Link2, Mail, MessageSquarePlus, MoreHorizontal, Phone, Tag, UserRound } from 'lucide-react';
import type { ReactNode } from 'react';
import type { CandidateBrowseRow } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { MenuItem, Popover, usePopover } from '../../../../components/Popover';
import { errorMessage } from '../../../../lib/api-client';
import { useToasts } from '../../../../stores/toasts';
import { useAddToCvBank, useCvBankAccess } from '../../../cv-bank/hooks/use-cv-bank-actions';
import { useAddCandidateTag } from '../../api/candidate-browse-api';
import type { RowHandlers } from './RowBits';
import { TagPicker, type TagChoice } from './TagPicker';
import styles from './QuickActions.module.css';

/** The quick actions a row shows on hover (mockup: Email, Call, Note, Tag, ⋯). */
export function RowQuickActions({ row, handlers }: { readonly row: CandidateBrowseRow; readonly handlers: RowHandlers }): ReactNode {
  return (
    <span className={styles.actions}>
      <LogButtons row={row} handlers={handlers} />
      {handlers.canEdit && (
        <Button variant="ghost" size="sm" icon={MessageSquarePlus} aria-label={`Add a note to ${row.candidate.fullName}`} title="Note" onClick={() => handlers.onOpen(row, { tab: 'activity', log: 'note' })} />
      )}
      {handlers.canEdit && <TagButton row={row} isIconOnly />}
      <MoreMenu row={row} />
    </span>
  );
}

/** Email and Call open the pop-up on Activity with its log bar on that kind. */
export function LogButtons({ row, handlers }: { readonly row: CandidateBrowseRow; readonly handlers: RowHandlers }): ReactNode {
  if (!handlers.canEdit) return null;
  const name = row.candidate.fullName;
  return (
    <>
      <Button variant="ghost" size="sm" icon={Mail} aria-label={`Email ${name}`} title="Email" onClick={() => handlers.onOpen(row, { tab: 'activity', log: 'email' })} />
      <Button variant="ghost" size="sm" icon={Phone} aria-label={`Call ${name}`} title="Call" onClick={() => handlers.onOpen(row, { tab: 'activity', log: 'call' })} />
    </>
  );
}

/** Tag: a picker anchored to the button; the tag shows on the row at once (§5a rule 1). */
export function TagButton({ row, isIconOnly = false }: { readonly row: CandidateBrowseRow; readonly isIconOnly?: boolean }): ReactNode {
  const { anchor, triggerRef, toggle, close } = usePopover();
  const addTag = useAddCandidateTag();
  const show = useToasts((state) => state.show);
  const pick = (choice: TagChoice): void => {
    close();
    addTag.mutate(
      { candidateId: row.candidate.id, input: choice.tagId ? { tagId: choice.tagId } : { name: choice.name }, optimistic: { id: choice.tagId ?? `pending-${choice.name}`, name: choice.name } },
      { onError: (error) => show({ tone: 'danger', message: "The tag wasn't added", detail: errorMessage(error) }) },
    );
  };
  return (
    <>
      <Button
        ref={triggerRef}
        variant={isIconOnly ? 'ghost' : 'outline'}
        size="sm"
        icon={Tag}
        aria-label={isIconOnly ? `Add a tag to ${row.candidate.fullName}` : undefined}
        title={isIconOnly ? 'Tag' : undefined}
        aria-haspopup="dialog"
        onClick={toggle}
      >
        {isIconOnly ? undefined : 'Tag'}
      </Button>
      <Popover anchor={anchor} onClose={close} align="right" label={`Tag ${row.candidate.fullName}`}>
        <TagPicker alreadyHas={row.tags.map((tag) => tag.id)} onPick={pick} />
      </Popover>
    </>
  );
}

/** ⋯: the full profile, the CV Bank (while it is on, for someone who may change candidates), and the link. */
export function MoreMenu({ row }: { readonly row: CandidateBrowseRow }): ReactNode {
  const { anchor, triggerRef, toggle, close } = usePopover();
  const { canEdit: canAddToBank } = useCvBankAccess();
  const { add } = useAddToCvBank();
  const show = useToasts((state) => state.show);
  const name = row.candidate.fullName;
  const path = `/candidates/${row.candidate.id}`;

  const copyLink = async (): Promise<void> => {
    close();
    try {
      await navigator.clipboard.writeText(new URL(path, document.baseURI).href);
      show({ tone: 'success', message: 'Link copied' });
    } catch {
      show({ tone: 'danger', message: "The link wasn't copied", detail: 'This browser blocked the clipboard. Try again from the page itself.' });
    }
  };

  return (
    <>
      <Button ref={triggerRef} variant="ghost" size="sm" icon={MoreHorizontal} aria-label={`More for ${name}`} title="More" aria-haspopup="menu" onClick={toggle} />
      <Popover anchor={anchor} onClose={close} align="right" label={`More for ${name}`}>
        <MenuItem icon={<UserRound size={14} />} href={path} onClick={close}>
          Open full profile
        </MenuItem>
        {canAddToBank && (
          <MenuItem
            icon={<Archive size={14} />}
            onClick={() => {
              close();
              void add([{ candidateId: row.candidate.id, name }], 'bulk');
            }}
          >
            Add to CV Bank
          </MenuItem>
        )}
        <MenuItem icon={<Link2 size={14} />} onClick={() => void copyLink()}>
          Copy link
        </MenuItem>
      </Popover>
    </>
  );
}
